/**
 * EditorToolbar: the Scene Builder shell (DOM).
 *
 * Lays out four regions, shown only while editing:
 *   - #editor-topbar  : transform mode (选择/移动/旋转/缩放) + placement mode
 *                       (吸附表面/自由深度/数值) + 导出 / 退出.
 *   - #editor-assets  : the asset library (one button per EditorAsset, grouped
 *                       by category) — click to begin placement.
 *   - #editor-sidebar : mounts for the property panel + object list (filled by
 *                       SceneEditor.attachPanels).
 *
 * Mode buttons reflect `EditorState` and mutate it on click. Asset buttons
 * highlight via `setPlacingActive` (the editor signals which asset is placing).
 */

import type { EditorState, EditorMode, PlacementMode } from '../editor/EditorState';
import type { EditorAssetLibrary, EditorAsset } from '../editor/EditorAssetLibrary';

export class EditorToolbar {
    private _el: HTMLElement;
    private _state: EditorState;
    private _assetLib: EditorAssetLibrary;
    private _onPlace: (assetId: string) => void;
    private _onSetSpawn: () => void;
    private _onSave: () => void;
    private _onExport: () => void;
    private _onExit: () => void;

    private _assetButtons = new Map<string, HTMLButtonElement>();
    private _modeButtons = new Map<EditorMode, HTMLButtonElement>();
    private _placeModeButtons = new Map<PlacementMode, HTMLButtonElement>();
    private _unsubscribe: () => void;

    /** Mount points handed to SceneEditor.attachPanels. */
    propertyMount!: HTMLElement;
    listMount!: HTMLElement;

    constructor(
        state: EditorState,
        assetLib: EditorAssetLibrary,
        onPlace: (assetId: string) => void,
        onSetSpawn: () => void,
        onSave: () => void,
        onExport: () => void,
        onExit: () => void
    ) {
        this._state = state;
        this._assetLib = assetLib;
        this._onPlace = onPlace;
        this._onSetSpawn = onSetSpawn;
        this._onSave = onSave;
        this._onExport = onExport;
        this._onExit = onExit;

        this._el = document.createElement('div');
        this._el.id = 'editor-shell';
        this._el.className = 'hidden';
        this._build();
        document.body.appendChild(this._el);

        this._unsubscribe = state.subscribe(() => this._sync());
        this._sync();
    }

    private _build(): void {
        // ── Top bar: grouped pills (变换 / 放置 / 操作) ──
        const top = document.createElement('div');
        top.id = 'editor-topbar';

        const transformGroup = this._group('变换');
        transformGroup.appendChild(this._modeBtn('选择', 'select'));
        transformGroup.appendChild(this._modeBtn('移动', 'move'));
        transformGroup.appendChild(this._modeBtn('旋转', 'rotate'));
        transformGroup.appendChild(this._modeBtn('缩放', 'scale'));
        top.appendChild(transformGroup);

        const placeGroup = this._group('放置');
        placeGroup.appendChild(this._placeModeBtn('吸附表面', 'raycastSurface'));
        placeGroup.appendChild(this._placeModeBtn('自由深度', 'freeDepth'));
        top.appendChild(placeGroup);

        const spacer = document.createElement('span');
        spacer.className = 'etb-spacer';
        top.appendChild(spacer);

        const actionGroup = this._group('操作');
        actionGroup.appendChild(this._actionBtn('保存', () => this._onSave()));
        actionGroup.appendChild(this._actionBtn('导出数据', () => this._onExport()));
        actionGroup.appendChild(this._actionBtn('退出 (Esc)', () => this._onExit(), true));
        top.appendChild(actionGroup);
        this._el.appendChild(top);

        // ── Left: configuration items (出生点 + placeable types) ──
        const assets = document.createElement('div');
        assets.id = 'editor-assets';
        const title = document.createElement('div');
        title.className = 'ea-title';
        title.textContent = '配置项目';
        assets.appendChild(title);

        // 出生点: a config action (not a placement) — sets the scene spawn.
        const spawnCard = document.createElement('button');
        spawnCard.className = 'ea-prop ea-config';
        spawnCard.innerHTML =
            '<span class="ea-name">设为出生点</span>' +
            '<span class="ea-desc">设置当前玩家位置为场景出生点</span>';
        spawnCard.title = '把当前玩家位置 / 朝向设为场景出生点';
        spawnCard.addEventListener('click', () => this._onSetSpawn());
        assets.appendChild(spawnCard);

        const skyboxCard = document.createElement('button');
        skyboxCard.className = 'ea-prop ea-config';
        skyboxCard.innerHTML =
            '<span class="ea-name">天空盒</span>' +
            '<span class="ea-desc">配置场景天空盒贴图和强度</span>';
        skyboxCard.title = '打开天空盒配置';
        skyboxCard.addEventListener('click', () => {
            this._state.setMode('select');
            this._state.showSceneSettings();
        });
        assets.appendChild(skyboxCard);

        const categories = new Map<string, EditorAsset[]>();
        for (const a of this._assetLib.list()) {
            const list = categories.get(a.category) ?? [];
            list.push(a);
            categories.set(a.category, list);
        }
        for (const [cat, list] of categories) {
            const group = document.createElement('div');
            group.className = 'ea-group';
            const label = document.createElement('div');
            label.className = 'ea-group-label';
            label.textContent = cat;
            group.appendChild(label);
            for (const a of list) {
                const btn = document.createElement('button');
                btn.className = 'ea-prop';
                btn.innerHTML =
                    `<span class="ea-name">${a.name}</span>` +
                    (a.description ? `<span class="ea-desc">${a.description}</span>` : '');
                btn.title = `放置 ${a.name}`;
                btn.addEventListener('click', () => {
                    this._onPlace(a.id);
                    this.setPlacingActive(a.id);
                });
                this._assetButtons.set(a.id, btn);
                group.appendChild(btn);
            }
            assets.appendChild(group);
        }
        this._el.appendChild(assets);

        // ── Right: property + object list mounts ──
        const side = document.createElement('div');
        side.id = 'editor-sidebar';
        const listWrap = document.createElement('div');
        listWrap.className = 'es-section es-section--list';
        const listTitle = document.createElement('div');
        listTitle.className = 'es-title';
        listTitle.textContent = '对象列表';
        listWrap.appendChild(listTitle);
        this.listMount = document.createElement('div');
        this.listMount.className = 'es-mount';
        listWrap.appendChild(this.listMount);
        side.appendChild(listWrap);

        const propWrap = document.createElement('div');
        propWrap.className = 'es-section';
        const propTitle = document.createElement('div');
        propTitle.className = 'es-title';
        propTitle.textContent = '属性';
        propWrap.appendChild(propTitle);
        this.propertyMount = document.createElement('div');
        this.propertyMount.className = 'es-mount';
        propWrap.appendChild(this.propertyMount);
        side.appendChild(propWrap);

        this._el.appendChild(side);
    }

    /** A labeled pill group: [label][btn][btn]… Callers append buttons to it. */
    private _group(label: string): HTMLElement {
        const g = document.createElement('div');
        g.className = 'etb-group';
        const lbl = document.createElement('span');
        lbl.className = 'etb-group-label';
        lbl.textContent = label;
        g.appendChild(lbl);
        return g;
    }

    private _modeBtn(label: string, mode: EditorMode): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn';
        b.textContent = label;
        b.addEventListener('click', () => this._state.setMode(mode));
        this._modeButtons.set(mode, b);
        return b;
    }

    private _placeModeBtn(label: string, mode: PlacementMode): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn';
        b.textContent = label;
        b.addEventListener('click', () => this._state.setPlacementMode(mode));
        this._placeModeButtons.set(mode, b);
        return b;
    }

    private _actionBtn(label: string, on: () => void, exit = false): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'etb-btn' + (exit ? ' etb-exit' : '');
        b.textContent = label;
        b.addEventListener('click', on);
        return b;
    }

    /** Sync mode/placement-mode button active states from EditorState. */
    private _sync(): void {
        for (const [m, b] of this._modeButtons) b.classList.toggle('active', this._state.mode === m);
        for (const [m, b] of this._placeModeButtons) b.classList.toggle('active', this._state.placementMode === m);
    }

    /** Highlight the asset currently being placed (or clear). */
    setPlacingActive(assetId: string | null): void {
        for (const [id, b] of this._assetButtons) b.classList.toggle('active', id === assetId);
    }

    show(): void { this._el.classList.remove('hidden'); }
    hide(): void { this._el.classList.add('hidden'); }

    destroy(): void { this._unsubscribe(); this._el.remove(); }
}
